import { Controller } from '@hotwired/stimulus';

/**
 * Multi-Step Modal Controller
 *
 * Manages step navigation within a single modal. Steps are div containers
 * toggled via CSS classes. Supports conditional step flow based on role
 * selection (collaborators need project assignment step, admins do not).
 *
 * Targets:
 * - step: Each step div container (0-indexed by position)
 * - stepIndicatorText: The <p> element displaying "Étape X/N — Label"
 * - nextButton: The "Suivant" button (shown when next step exists)
 * - submitButton: The direct submit button (shown when no next step needed)
 * - backButton: The "Retour" button
 *
 * Values:
 * - currentStep: Current step index, 0-based (default: 0)
 * - totalSteps: Total number of steps (default: 2)
 * - stepLabels: Array of labels for each step (e.g. ["Informations", "Chantiers"])
 *
 * Actions:
 * - next: Advance to the next step
 * - back: Return to the previous step
 * - roleChanged: Toggle next/submit buttons and step indicator based on selected role
 *
 * @example
 * <div data-controller="multi-step-modal"
 *      data-multi-step-modal-total-steps-value="2"
 *      data-multi-step-modal-step-labels-value='["Informations", "Chantiers"]'>
 *   <div data-multi-step-modal-target="stepIndicatorText" class="mb-4">
 *     <p class="text-sm font-medium text-primary-600 bg-primary-50 rounded-md px-3 py-1.5 inline-block">
 *       Étape 1/2 — Informations
 *     </p>
 *   </div>
 *
 *   <div data-multi-step-modal-target="step">
 *     <select data-action="change->multi-step-modal#roleChanged">...</select>
 *     <button data-action="multi-step-modal#next"
 *             data-multi-step-modal-target="nextButton">Suivant</button>
 *     <button type="submit"
 *             data-multi-step-modal-target="submitButton" class="hidden">Envoyer</button>
 *   </div>
 *
 *   <div data-multi-step-modal-target="step" class="hidden">
 *     <button data-action="multi-step-modal#back"
 *             data-multi-step-modal-target="backButton">Retour</button>
 *     <button type="submit">Envoyer l'invitation</button>
 *   </div>
 * </div>
 */
export default class extends Controller {
  static targets = ['step', 'stepIndicatorText', 'nextButton', 'submitButton', 'backButton'];
  static values = {
    currentStep: { type: Number, default: 0 },
    totalSteps: { type: Number, default: 2 },
    stepLabels: { type: Array, default: [] },
  };

  connect() {
    this.#showStep(this.currentStepValue);
  }

  next() {
    if (this.currentStepValue < this.totalStepsValue - 1) {
      if (!this.#validateCurrentStep()) return;
      this.currentStepValue++;
    }
  }

  back() {
    if (this.currentStepValue > 0) {
      this.currentStepValue--;
    }
  }

  currentStepValueChanged() {
    this.#showStep(this.currentStepValue);
  }

  roleChanged(event) {
    const role = event.target.value;
    const needsProjectStep = role === 'collaborator';

    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.toggle('hidden', !needsProjectStep);
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.classList.toggle('hidden', needsProjectStep);
    }
    if (this.hasStepIndicatorTextTarget) {
      this.stepIndicatorTextTarget.classList.toggle('invisible', !needsProjectStep);
    }

    // Reset to first step when role changes
    this.currentStepValue = 0;
  }

  // Private

  #showStep(index) {
    this.stepTargets.forEach((step, i) => {
      step.classList.toggle('hidden', i !== index);
    });

    this.#updateStepIndicator(index);
  }

  #validateCurrentStep() {
    const currentStep = this.stepTargets[this.currentStepValue];
    if (!currentStep) return true;

    const inputs = currentStep.querySelectorAll('input, select, textarea');
    for (const input of inputs) {
      if (!input.reportValidity()) return false;
    }
    return true;
  }

  #updateStepIndicator(index) {
    if (!this.hasStepIndicatorTextTarget) return;
    const label = this.stepLabelsValue[index] || '';
    const separator = label ? ' — ' : '';
    const text = `Étape ${index + 1}/${this.totalStepsValue}${separator}${label}`;
    const p = this.stepIndicatorTextTarget.querySelector('p');
    if (p) {
      p.textContent = text;
    } else {
      this.stepIndicatorTextTarget.textContent = text;
    }
  }
}
